/**
 * Pure helpers for Settings > Audit log (tracker item 32): action labels,
 * target resolution and row links, time display, and filter error messages.
 */

import * as types from '../../api/types';
import { getErrorCode, getErrorMessage } from '../../utils/apiError';

export const AUDIT_LOG_PATH = '/settings/audit-log';
export const AUDIT_LOG_PAGE_SIZE = 50;
/** No per-user route exists, so user targets link to the Users list. */
export const USERS_SETTINGS_PATH = '/settings/users';

const ACTION_LABELS: Record<types.AuditAction, string> = {
  'user.create': 'User created',
  'user.update': 'User updated',
  'user.deactivate': 'User deactivated',
  'user.reactivate': 'User reactivated',
  'user.reset_password': 'User password reset',
  'user.role_change': 'User role changed',
  'user.self_password_change': 'Own password changed',
  'customer.lead_source_change': 'Customer lead source changed',
  'price_chart.create': 'Price chart created',
  'price_chart.update': 'Price chart updated',
  'price_chart.tiers_replace': 'Price chart bands replaced',
  'price_chart.delete': 'Price chart deleted',
  'checklist_template.create': 'Checklist template created',
  'checklist_template.update': 'Checklist template changed',
  'checklist_template.deactivate': 'Checklist template deactivated',
  'checklist_template.delete': 'Checklist template deleted',
  'job_checklist.apply': 'Job checklist template applied',
  'job_checklist.remove_group': 'Job checklist group removed',
  'job_checklist.step_add': 'Job checklist step added',
  'job_checklist.step_update': 'Job checklist step changed',
  'job_checklist.step_delete': 'Job checklist step deleted',
  'job_checklist.step_complete': 'Job checklist step ticked',
  'job_checklist.step_uncomplete': 'Job checklist step unticked',
  'attachment.upload': 'Attachment uploaded',
  'attachment.update': 'Attachment renamed or described',
  'attachment.delete': 'Attachment deleted',
  'company_settings.update': 'Company settings updated',
  'invoice.send': 'Invoice sent',
  'invoice.mark_paid': 'Invoice marked paid',
  'invoice.cancel': 'Invoice cancelled',
  'payment.void': 'Payment voided',
  'payment.credit_apply': 'Credit applied to invoice',
  'payment.credit_unapply': 'Applied credit removed',
  'gl.account_create': 'GL account created',
  'gl.account_update': 'GL account updated',
  'gl.journal_entry_manual': 'Manual journal entry posted',
  'gl.journal_entry_reverse': 'Journal entry reversed',
  'discount.create': 'Discount created',
  'discount.update': 'Discount updated',
  'discount.delete': 'Discount deleted',
  'tax_rate.create': 'Tax rate created',
  'tax_rate.update': 'Tax rate updated',
  'tax_rate.delete': 'Tax rate deleted',
  'finance_charge.assess': 'Finance charge assessed',
  'flat_contract.bill': 'Flat contract billed',
  'auto_invoice.create': 'Invoice auto-created',
  'dunning.notice': 'Overdue reminder',
  'dunning.settings_update': 'Reminder schedule changed',
  'inventory.receive': 'Stock received',
  'inventory.adjust': 'Stock count adjusted',
  'notification.send': 'Message sent',
  'notification.retry': 'Message retried',
  'notification.cancel': 'Message cancelled',
  'notification.template_update': 'Message template changed',
  'notification.provider_update': 'Sending connection changed',
  'notification.provider_verify': 'Sending connection verified',
  'job.schedule': 'Job scheduled (dispatch)',
  'job.start': 'Job started (technician)',
  'job.stop': 'Job timer stopped',
  'job.finish': 'Job finished (technician)',
  'job.signature': 'Customer signature captured',
  'job.time_entry.create': 'Time entry added',
  'job.time_entry.update': 'Time entry changed',
  'job.time_entry.delete': 'Time entry deleted',
  'job.tech_note': 'Technician note added',
  'work_order.prepare': 'Work order batch prepared',
  'work_order.print': 'Work orders sent to print',
  'work_order.confirm': 'Work orders confirmed printed',
  'online_payments.connect': 'Payment processor connected',
  'online_payments.disconnect': 'Payment processor disconnected',
  'online_payments.revoked': 'Payment processor access revoked',
  'online_payments.location': 'Payment location changed',
  'online_payments.token_refresh': 'Payment processor access renewed',
  'online_payment.charge': 'Online card charge',
  'online_payment.refund': 'Online payment refunded',
  'online_payment.reconcile': 'Online payment reconciled',
  'payment_method.add': 'Card on file added',
  'payment_method.remove': 'Card on file removed',
  'payment_method.default': 'Default card changed',
  'autopay.enable': 'Autopay turned on',
  'autopay.disable': 'Autopay turned off',
  'portal.register': 'Portal registration',
  'portal.verify_email': 'Portal email verified',
  'portal.auto_link': 'Portal account linked automatically',
  'portal.invite': 'Portal invitation sent',
  'portal.link': 'Portal account linked by staff',
  'portal.unlink': 'Portal account unlinked',
  'portal.disable': 'Portal access disabled',
  'portal.decline': 'Portal access request declined',
  'portal.profile_update': 'Portal profile updated',
  'invoice.portal_code_regenerate': 'Invoice portal code regenerated',
  'portal.payment_method_add': 'Card added in the portal',
  'portal.payment_method_remove': 'Card removed in the portal',
  'portal.autopay_consent': 'Autopay consent changed in the portal',
  'portal.service_request': 'Service requested in the portal',
  'service_request.convert': 'Service request converted to a job',
  'service_request.decline': 'Service request declined',
  'portal.reschedule_request': 'Reschedule requested in the portal',
  'portal.reschedule_cancel': 'Reschedule request cancelled',
  'reschedule.approve': 'Reschedule request approved',
  'reschedule.decline': 'Reschedule request declined',
  // Estimates (tracker item 39)
  'estimate.create': 'Estimate created',
  'estimate.update': 'Estimate edited',
  'estimate.send': 'Estimate sent',
  'estimate.resend': 'Estimate resent',
  'estimate.approve': 'Estimate approved',
  'estimate.decline': 'Estimate declined',
  'estimate.cancel': 'Estimate cancelled',
  'estimate.convert': 'Estimate converted',
  'survey.settings_update': 'Survey settings changed',
  'survey.send': 'Customer survey sent',
  'survey.resend': 'Customer survey sent again',
  'portal_rollout.create': 'Portal rollout campaign created',
  'portal_rollout.start': 'Portal rollout campaign started',
  'portal_rollout.pause': 'Portal rollout campaign paused',
  'portal_rollout.resume': 'Portal rollout campaign resumed',
  'portal_rollout.cancel': 'Portal rollout campaign cancelled',
  'portal_rollout.send': 'Portal rollout invitations sent',
  'portal_rollout.resend': 'Portal rollout invitation sent again',
  'portal_rollout.activate': 'Portal invitation accepted',
  'data.import': 'Data imported from CSV',
  'data.export': 'Data exported',
  'conversation.create': 'Conversation started',
  'conversation.reply': 'Conversation reply sent',
  'conversation.note': 'Internal note added',
  'conversation.update': 'Conversation updated',
  'conversation.assign_customer': 'Email attached to a customer',
  'conversation.discard': 'Unmatched email discarded',
  'portal.message': 'Portal message sent',
  'property.geocode_manual': 'Property location set by hand',
  'geocoding.retry_failed': 'Failed addresses geocoded again',
  'mfa.enroll': 'Two-step verification turned on',
  'mfa.disable': 'Two-step verification turned off',
  'mfa.reset': 'Two-step verification reset by an admin',
  'mfa.recovery_codes_regenerate': 'New recovery codes created',
  'mfa.recovery_code_used': 'Recovery code used',
  'mfa.lockout': 'Locked after too many wrong codes',
  'setup.skip': 'Setup step skipped',
  'setup.unskip': 'Setup step unskipped',
  'setup.mark_done': 'Setup step marked done',
  'setup.unmark_done': 'Setup step unmarked',
  'setup.dismiss': 'Setup checklist hidden',
  'setup.restore': 'Setup checklist shown again',
  'setup.reminder': 'Setup reminder emailed',
  'starter.apply': 'Starter pack applied',
  'starter.remove': 'Unused starter records removed',
  'starter.keep': 'Starter record kept',
  'recurring_preset.create': 'Recurring preset created',
  'recurring_preset.update': 'Recurring preset updated',
  'recurring_preset.delete': 'Recurring preset deleted',
};

/** Human label for an action; an action this client does not know shows as-is. */
export function auditActionLabel(action: string): string {
  return Object.prototype.hasOwnProperty.call(ACTION_LABELS, action) ? ACTION_LABELS[action as types.AuditAction] : action;
}

const TARGET_TYPE_LABELS: Record<string, string> = {
  user: 'User',
  customer: 'Customer',
  invoice: 'Invoice',
  payment: 'Payment',
  gl_account: 'GL account',
  journal_entry: 'Journal entry',
  discount: 'Discount',
  tax_rate: 'Tax rate',
  item: 'Item',
  job: 'Job',
  property: 'Property',
  checklist_template: 'Checklist template',
  conversation: 'Conversation',
  recurring_preset: 'Recurring preset',
};

export function auditTargetTypeLabel(type: string): string {
  return TARGET_TYPE_LABELS[type] || type;
}

export interface AuditTarget {
  /** user, customer, or a details.target type (invoice, payment, gl_account, …) */
  type: string;
  id: string;
  /** Human reference (email, invoice number, account number, …); falls back to the id */
  label: string;
  /** Route to the target, or null when it has no page */
  to: string | null;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

const nonEmptyString = (value: unknown): value is string => typeof value === 'string' && value.length > 0;

/** Route for a target, when it has one. Only routable targets get a link. */
export function auditTargetPath(type: string, id: string): string | null {
  const encoded = encodeURIComponent(id);
  switch (type) {
    case 'invoice':
      return `/invoices/${encoded}`;
    case 'payment':
      return `/payments/${encoded}`;
    case 'journal_entry':
      return `/ledger/${encoded}`;
    case 'customer':
      return `/customers/${encoded}`;
    case 'property':
      // Property pages live under their customer; /properties/:id looks it up and redirects
      return `/properties/${encoded}`;
    case 'job':
      return `/jobs/${encoded}`;
    case 'estimate':
      return `/estimates/${encoded}`;
    case 'user':
      return USERS_SETTINGS_PATH;
    default:
      return null;
  }
}

/**
 * What a row is about: the target user (targetUserId / targetEmail), else
 * `details.target = { type, id, label }`, else the customer of a lead-source
 * change (`details.customerId`). Null when the row names no target (e.g.
 * company settings, price charts).
 */
export function auditTarget(
  row: Pick<types.AuditLogEntry, 'action' | 'targetUserId' | 'targetEmail' | 'details'>
): AuditTarget | null {
  if (row.targetUserId || row.targetEmail) {
    const id = row.targetUserId || '';
    return { type: 'user', id, label: row.targetEmail || id, to: id ? auditTargetPath('user', id) : null };
  }
  const details = row.details;
  if (isRecord(details)) {
    const target = details.target;
    if (isRecord(target) && nonEmptyString(target.type) && nonEmptyString(target.id)) {
      const label = nonEmptyString(target.label) ? target.label : target.id;
      return { type: target.type, id: target.id, label, to: auditTargetPath(target.type, target.id) };
    }
    if (row.action === 'customer.lead_source_change' && nonEmptyString(details.customerId)) {
      return { type: 'customer', id: details.customerId, label: details.customerId, to: auditTargetPath('customer', details.customerId) };
    }
  }
  return null;
}

/** Pretty JSON for the details expander, or null when nothing was recorded. */
export function auditDetailsJson(details: unknown): string | null {
  if (details === null || details === undefined) return null;
  try {
    return JSON.stringify(details, null, 2);
  } catch {
    return String(details);
  }
}

/**
 * Timestamp for the Time column, in the viewer's time zone unless one is
 * given (tests pass 'UTC'). Unparseable values are shown as-is.
 */
export function auditTime(value: string, timeZone?: string): string {
  const date = new Date(value);
  if (!value || Number.isNaN(date.getTime())) return value || '—';
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: 'numeric',
    minute: '2-digit',
    ...(timeZone ? { timeZone } : {}),
  });
}

/** Actor select option label: the email, marked when the user is deactivated. */
export function actorOptionLabel(user: Pick<types.UserListItem, 'email' | 'isActive'>): string {
  return user.isActive ? user.email : `${user.email} (inactive)`;
}

/**
 * Message for a failed list load. A 400 INVALID_FILTER (unknown action, bad
 * date, inverted range) gets a friendly explanation instead of a raw error.
 */
export function auditLogErrorMessage(cause: unknown): string {
  if (getErrorCode(cause) === 'INVALID_FILTER') {
    return 'These filters are not valid (for example an unknown action or an invalid date). Change or clear the filters and try again.';
  }
  return getErrorMessage(cause, 'Could not load the audit log');
}
